import { StaticImageData } from 'next/image';
import { useMemo } from 'react';

import { useFilters } from '@/features/filters/hooks/use-filters';
import { transformOptions } from '@/features/listing/utils/filters';

export interface Category {
  id: string;
  name: string;
  featured?: {
    name: string;
    href: string;
    imageSrc: StaticImageData | string;
    imageAlt: string;
    className?: string;
  }[];
  sections?: {
    id: string;
    name: string;
    items: { name: string; href: string }[];
  }[];
}

export const useNavigation = () => {
  const { data: filters } = useFilters();

  const navigation = useMemo(() => {
    if (!filters) {
      return {
        categories: [
          {
            id: 'mens',
            name: 'Menswear',
          },
          {
            id: 'womens',
            name: 'Womenswear',
          },
        ],
      };
    }

    // Transform options for occasions
    const occasionOptions = transformOptions(filters.occasions);

    // Helper to transform categories by gender
    const transformCategories = (gender: 'mens' | 'womens') => {
      return [
        ...(filters.categories
          .filter((category) => category.gender === gender)
          .map((category) => ({ name: category.name, href: category.slug })) ||
          []),
        { name: 'Browse All', href: '' },
      ];
    };

    // Transformed categories
    const mensCategories = transformCategories('mens');
    const womensCategories = transformCategories('womens');

    // Common structure for categories
    const buildCategory = (
      id: string,
      name: string,
      featured: {
        name: string;
        href: string;
        imageSrc: string;
        imageAlt: string;
        className?: string;
      }[],
      categories: { name: string; href: string }[],
    ) => ({
      id,
      name,
      featured,
      sections: [
        {
          id: 'category',
          name: 'Shop by Category',
          items: categories,
        },
        {
          id: 'occasion',
          name: 'Shop by Occasion',
          items: [...occasionOptions, { name: 'Browse All', href: '' }],
        },
      ],
    });

    return {
      categories: [
        buildCategory(
          'mens',
          'Menswear',
          [
            {
              name: 'New',
              href: '/search/mens?conditions=new',
              imageSrc: '/images/home/mens-new.webp',
              imageAlt: 'New clothing.',
            },
            {
              name: 'Pre-loved',
              href: '/search/mens?conditions=pre-loved-excellent,pre-loved-good,pre-loved-fair',
              imageSrc: '/images/home/our-story.webp',
              imageAlt: 'Pre-loved clothing.',
            },
          ],
          mensCategories,
        ),
        buildCategory(
          'womens',
          'Womenswear',
          [
            {
              name: 'New',
              href: '/search/womens?conditions=new',
              imageSrc: '/images/home/womens-new.webp',
              imageAlt:
                'Models sitting back to back, wearing Basic Tee in black and bone.',
            },
            {
              name: 'Pre-loved',
              className: 'object-center rotate-90',
              href: '/search/womens?conditions=pre-loved-excellent,pre-loved-good,pre-loved-fair',
              imageSrc: '/images/home/womens-pre-loved.webp',
              imageAlt:
                'Close up of Basic Tee fall bundle with off-white, ochre, olive, and black tees.',
            },
          ],
          womensCategories,
        ),
      ],
    };
  }, [filters]);

  return navigation;
};
